<app-page-shell>
    <p-toast position="bottom-center"></p-toast>

    <app-page-hero eyebrow="Accounting Setup" title="Chart of Accounts"
        subtitle="Maintain the company accounting structure used by journals, invoices, bills, bank reconciliation, and logistics posting.">
    </app-page-hero>

    <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <app-page-data-panel title="Account Tree" subtitle="Posting account hierarchy for the selected company"
        [resultText]="resultText">
        <div class="coa-filter-bar" role="search" aria-label="Filter chart of accounts">
            <label class="coa-filter-field coa-search-field" for="accountSearch">
                <span>Search accounts</span>
                <span class="coa-search-input">
                    <i class="pi pi-search" aria-hidden="true"></i>
                    <input id="accountSearch" type="search" pInputText [(ngModel)]="searchQuery"
                        (ngModelChange)="applyFilters()" placeholder="Name, code, type, or internal type"
                        autocomplete="off" />
                </span>
            </label>
            <label class="coa-filter-field" for="accountTypeFilter">
                <span>Account type</span>
                <p-select inputId="accountTypeFilter" [options]="accountTypeOptions" optionLabel="label"
                    optionValue="value" placeholder="All types" [showClear]="true" [filter]="true"
                    [(ngModel)]="selectedAccountTypeId" (onChange)="applyFilters()" styleClass="coa-filter-select" />
            </label>
            <label class="coa-filter-field" for="accountStatusFilter">
                <span>Status</span>
                <p-select inputId="accountStatusFilter" [options]="statusOptions" optionLabel="label"
                    optionValue="value" placeholder="All statuses" [showClear]="true"
                    [(ngModel)]="selectedStatus" (onChange)="applyFilters()" styleClass="coa-filter-select" />
            </label>
            <label class="coa-filter-field" for="accountCurrencyFilter">
                <span>Currency</span>
                <p-select inputId="accountCurrencyFilter" [options]="currencyOptions" optionLabel="label"
                    optionValue="value" placeholder="All currencies" [showClear]="true"
                    [(ngModel)]="selectedCurrency" (onChange)="applyFilters()" styleClass="coa-filter-select" />
            </label>
            <label class="coa-filter-field" for="accountReconciliationFilter">
                <span>Reconciliation</span>
                <p-select inputId="accountReconciliationFilter" [options]="yesNoOptions" optionLabel="label"
                    optionValue="value" placeholder="Any" [showClear]="true"
                    [(ngModel)]="selectedReconciliation" (onChange)="applyFilters()" styleClass="coa-filter-select" />
            </label>
            <label class="coa-filter-field" for="accountProtectedFilter">
                <span>Posting dependency</span>
                <p-select inputId="accountProtectedFilter" [options]="yesNoOptions" optionLabel="label"
                    optionValue="value" placeholder="Any" [showClear]="true"
                    [(ngModel)]="selectedProtected" (onChange)="applyFilters()" styleClass="coa-filter-select" />
            </label>
            <p-button class="coa-clear-filters" label="Clear filters" icon="pi pi-filter-slash"
                severity="secondary" [outlined]="true" [disabled]="!hasActiveFilters"
                (onClick)="clearFilters()" />
        </div>
        <p-treeTable [value]="chartOfAccountsTree" dataKey="accountId" styleClass="coa-table"
            [tableStyle]="{ 'min-width': '62rem' }">
            <ng-template pTemplate="header">
                <tr>
                    <th>Account</th>
                    <th class="balance-col">Balance</th>
                    <th>Details</th>
                    <th class="actions-col">Actions</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-rowNode let-rowData="rowData">
                <tr [ttRow]="rowNode" [class.root-row]="accountDepth(rowNode) === 0">
                    <td class="account-col">
                        <div class="account-tree-cell" [style.--coa-depth]="accountDepth(rowNode)">
                            <span class="tree-guide" aria-hidden="true"></span>
                            <p-treeTableToggler [rowNode]="rowNode"></p-treeTableToggler>
                            <div class="account-copy">
                                <button type="button" class="account-title-button" (click)="viewChartOfAccounts(rowData)">
                                    {{ rowData.accountName }}
                                </button>
                                <div class="account-meta-line">
                                    <span class="account-code">{{ rowData.accountCode }}</span>
                                    <span class="account-depth">Level {{ accountDepth(rowNode) + 1 }}</span>
                                    <span *ngIf="childAccountCount(rowData)" class="child-count">
                                        {{ hasActiveFilters ? (rowNode.node?.children?.length || 0) + ' of ' : '' }}{{ childAccountCount(rowData) }} sub-accounts
                                    </span>
                                    <p-tag *ngIf="isProtectedPostingAccount(rowData)" value="Posting dependency"
                                        severity="warn" styleClass="compact-tag"></p-tag>
                                </div>
                            </div>
                        </div>
                    </td>
                    <td class="balance-col">
                        <div class="balance-stack" [class.credit]="isCreditBalance(rowData)"
                            [class.zero]="isZeroBalance(rowData)">
                            <span class="balance-value">
                                {{ rowData.currencyCode || 'SAR' }} {{ balanceMagnitude(rowData) | number:'1.2-2' }}
                            </span>
                            <span class="balance-side">{{ balanceSide(rowData) }}</span>
                        </div>
                    </td>
                    <td class="details-col">
                        <div class="detail-stack">
                            <div class="detail-primary">{{ rowData.accountType?.accountTypeName || '-' }}</div>
                            <div class="detail-muted">
                                {{ rowData.internalType || '-' }} · {{ rowData.currencyCode || '-' }}
                            </div>
                            <div class="detail-tags">
                                <p-tag [value]="accountStatus(rowData)" [severity]="accountStatusSeverity(rowData)"
                                    styleClass="compact-tag"></p-tag>
                                <p-tag [value]="rowData.allowReconciliation ? 'Reconciliation' : 'No reconciliation'"
                                    [severity]="rowData.allowReconciliation ? 'success' : 'secondary'"
                                    styleClass="compact-tag"></p-tag>
                            </div>
                        </div>
                    </td>
                    <td class="actions-col">
                        <p-button icon="pi pi-eye" severity="secondary" [text]="true" [rounded]="true"
                            pTooltip="View account" (onClick)="viewChartOfAccounts(rowData)"></p-button>
                        <p-button icon="pi pi-plus" severity="success" [text]="true" [rounded]="true"
                            pTooltip="Add sub-account" (onClick)="addChildChartOfAccounts(rowData)"></p-button>
                        <p-button *ngIf="rowData.allowEditing" icon="pi pi-pencil" severity="info" [text]="true"
                            [rounded]="true" pTooltip="Edit account" (onClick)="editChartOfAccounts(rowData)"></p-button>
                        <p-button *ngIf="rowData.allowEditing" icon="pi pi-trash" severity="danger" [text]="true"
                            [rounded]="true" pTooltip="Delete account" (onClick)="deleteChartOfAccounts(rowData)"></p-button>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="4" class="empty-cell">
                        {{ hasActiveFilters ? 'No accounts match these filters.' : 'No chart of accounts found.' }}
                    </td>
                </tr>
            </ng-template>
        </p-treeTable>
    </app-page-data-panel>
</app-page-shell>
